import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchEventImages,
  fetchEventImageActivities,
} from "../../../../api/eventImages";
import "./EventImagePicker.css";

const S3_BASE = "https://s3.amazonaws.com/social-images-public/";
const ALL_FILTER = "All";
const LEGACY_FILTER = "Legacy";

const buildImgUrl = (path) => `${S3_BASE}${path}`;

const getDisplayName = (item) =>
  item?.displayName || item?.alias || item?.s3Key || "";

const compareUploadedAtDesc = (a, b) => {
  const at = a?.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
  const bt = b?.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
  return bt - at;
};

// Within an activity group, favorites bubble to the top, then most-recently
// uploaded. The server already sorts favorites-first globally, but the
// per-activity grouping below splits the list into Maps so we re-apply the
// favorites-first comparator here to preserve order inside each section.
const compareFavoritesThenUploadedAtDesc = (a, b) => {
  const af = a?.favorite ? 1 : 0;
  const bf = b?.favorite ? 1 : 0;
  if (af !== bf) return bf - af;
  return compareUploadedAtDesc(a, b);
};

const EventImagePicker = ({ value, onChange, legacyOptions = [] }) => {
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);
  const [remoteItems, setRemoteItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const containerRef = useRef(null);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([fetchEventImageActivities(), fetchEventImages({})])
      .then(([actsResp, imgsResp]) => {
        if (cancelled) return;
        setActivities(
          Array.isArray(actsResp?.activities) ? actsResp.activities : []
        );
        setRemoteItems(Array.isArray(imgsResp?.items) ? imgsResp.items : []);
        setLoading(false);
        setInitialFetchDone(true);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("EventImagePicker fetch error:", err);
        setError(err);
        setActivities([]);
        setRemoteItems([]);
        setLoading(false);
        setInitialFetchDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // If initial fetch yielded nothing, default to Legacy filter so user sees something useful.
  useEffect(() => {
    if (!initialFetchDone) return;
    if (activities.length === 0 && remoteItems.length === 0) {
      setActiveFilter(LEGACY_FILTER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFetchDone]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  // Group remote items by activity
  const itemsByActivity = useMemo(() => {
    const map = new Map();
    remoteItems.forEach((item) => {
      const act = item?.activity || "Other";
      if (!map.has(act)) map.set(act, []);
      map.get(act).push(item);
    });
    map.forEach((arr) => arr.sort(compareFavoritesThenUploadedAtDesc));
    return map;
  }, [remoteItems]);

  // Build sorted activity chip list — union of S3 folders and activities
  // present on actual items, so every item is reachable via a filter chip.
  const sortedActivityChips = useMemo(() => {
    const set = new Set(activities);
    itemsByActivity.forEach((_, act) => set.add(act));
    return [...set].sort((a, b) => String(a).localeCompare(String(b)));
  }, [activities, itemsByActivity]);

  // Activity sections sorted by max(uploadedAt) of their items, descending
  const activitySectionsForAll = useMemo(() => {
    const entries = [];
    itemsByActivity.forEach((items, activity) => {
      if (!items || items.length === 0) return;
      const maxUploadedAt = items.reduce((acc, it) => {
        const t = it?.uploadedAt ? new Date(it.uploadedAt).getTime() : 0;
        return t > acc ? t : acc;
      }, 0);
      entries.push({ activity, items, maxUploadedAt });
    });
    entries.sort((a, b) => b.maxUploadedAt - a.maxUploadedAt);
    return entries;
  }, [itemsByActivity]);

  // Find display info for current value (trigger button)
  const triggerInfo = useMemo(() => {
    if (!value) {
      return { name: "Select Image", thumbUrl: null };
    }
    const remoteMatch = remoteItems.find((it) => it?.s3Key === value);
    if (remoteMatch) {
      return {
        name: getDisplayName(remoteMatch),
        thumbUrl: buildImgUrl(value),
      };
    }
    const legacyMatch = legacyOptions.find((opt) => opt.path === value);
    if (legacyMatch) {
      return {
        name: legacyMatch.alias,
        thumbUrl: buildImgUrl(value),
      };
    }
    return { name: value, thumbUrl: buildImgUrl(value) };
  }, [value, remoteItems, legacyOptions]);

  const handleSelect = (path) => {
    onChange(path);
    setOpen(false);
  };

  const renderRemoteRow = (item) => {
    const path = item?.s3Key;
    const displayName = getDisplayName(item);
    const isSelected = value === path;
    return (
      <button
        key={path || displayName}
        type="button"
        className={`event-image-picker__row${
          isSelected ? " event-image-picker__row--selected" : ""
        }`}
        onClick={() => handleSelect(path)}
        aria-label={displayName}
      >
        <span className="event-image-picker__row-name">
          {item?.favorite ? (
            <span
              className="event-image-picker__row-star"
              aria-label="Favorite"
              title="Favorite"
            >
              ★
            </span>
          ) : null}
          {displayName}
        </span>
        {item?.activity && activeFilter === ALL_FILTER ? (
          <span className="event-image-picker__row-pill">{item.activity}</span>
        ) : null}
      </button>
    );
  };

  const renderLegacyRow = (opt) => {
    const isSelected = value === opt.path;
    return (
      <button
        key={opt.path}
        type="button"
        className={`event-image-picker__row${
          isSelected ? " event-image-picker__row--selected" : ""
        }`}
        onClick={() => handleSelect(opt.path)}
        aria-label={opt.alias}
      >
        <span className="event-image-picker__row-name">{opt.alias}</span>
      </button>
    );
  };

  const renderLegacySection = () => (
    <div key="__legacy__">
      <div className="event-image-picker__section-header">Legacy</div>
      {legacyOptions.map(renderLegacyRow)}
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="event-image-picker__placeholder">Loading…</div>
      );
    }

    if (activeFilter === LEGACY_FILTER) {
      return renderLegacySection();
    }

    if (activeFilter === ALL_FILTER) {
      return (
        <>
          {activitySectionsForAll.map(({ activity, items }) => (
            <div key={activity}>
              <div className="event-image-picker__section-header">
                {activity}
              </div>
              {items.map(renderRemoteRow)}
            </div>
          ))}
          {renderLegacySection()}
        </>
      );
    }

    // Specific activity filter — flat list, newest-first, no headers
    const items = itemsByActivity.get(activeFilter) || [];
    if (items.length === 0) {
      return (
        <div className="event-image-picker__placeholder">
          No images for this activity yet.
        </div>
      );
    }
    return <>{items.map(renderRemoteRow)}</>;
  };

  return (
    <div className="event-image-picker" ref={containerRef}>
      <button
        type="button"
        className="event-image-picker__trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Selected event image: ${triggerInfo.name}`}
      >
        {triggerInfo.thumbUrl ? (
          <img
            src={triggerInfo.thumbUrl}
            alt=""
            className="event-image-picker__trigger-thumb"
            loading="lazy"
          />
        ) : (
          <div
            className="event-image-picker__trigger-thumb"
            aria-hidden="true"
          />
        )}
        <span className="event-image-picker__trigger-text">
          <span className="event-image-picker__trigger-name">
            {triggerInfo.name}
          </span>
          <span className="event-image-picker__trigger-hint">
            Click to change
          </span>
        </span>
        <span className="event-image-picker__caret" aria-hidden="true">
          {open ? "▲" : "▼"}
        </span>
      </button>

      {value ? (
        <img
          src={buildImgUrl(value)}
          alt="Event preview"
          className="event-image-picker__large-preview"
          loading="lazy"
        />
      ) : null}

      {open && (
        <div className="event-image-picker__popover" role="dialog">
          <div
            className="event-image-picker__chips"
            role="tablist"
            aria-label="Filter images"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeFilter === ALL_FILTER}
              className={`event-image-picker__chip${
                activeFilter === ALL_FILTER
                  ? " event-image-picker__chip--active"
                  : ""
              }`}
              onClick={() => setActiveFilter(ALL_FILTER)}
            >
              All
            </button>
            {sortedActivityChips.map((act) => (
              <button
                key={act}
                type="button"
                role="tab"
                aria-selected={activeFilter === act}
                className={`event-image-picker__chip${
                  activeFilter === act
                    ? " event-image-picker__chip--active"
                    : ""
                }`}
                onClick={() => setActiveFilter(act)}
              >
                {act}
              </button>
            ))}
            <button
              type="button"
              role="tab"
              aria-selected={activeFilter === LEGACY_FILTER}
              className={`event-image-picker__chip${
                activeFilter === LEGACY_FILTER
                  ? " event-image-picker__chip--active"
                  : ""
              }`}
              onClick={() => setActiveFilter(LEGACY_FILTER)}
            >
              Legacy
            </button>
          </div>

          {error && (
            <div className="event-image-picker__error">
              Could not load images. Showing legacy options only.
            </div>
          )}

          <div className="event-image-picker__body">{renderBody()}</div>
        </div>
      )}
    </div>
  );
};

export default EventImagePicker;
